import { assert, assertType, CustomError } from 'cadenza/utils/custom-error';
import type { EventKey } from 'cadenza/utils/event-util';
import { Observable, on, unByKey } from 'cadenza/utils/event-util';
import { uniqueId } from 'cadenza/utils/unique-id';

const ID_PREFIX = 'cadenza'; // Property in messages to clearly identify them as one of ours (not browser!)

export const MessageTypes = {
  /**
   * Initialize the communication by sending the endpoint to communicate over.
   */
  INIT: 'INIT',

  /**
   * Called when a dashlet is activated or deactivated.
   */
  ACTIVATE: 'ACTIVATE',

  /**
   * Reload requested event.
   */
  RELOAD: 'RELOAD'
} as const;

export type MessageType = typeof MessageTypes[keyof typeof MessageTypes];

/**
 * A transferable that to be sent over the post message API.
 */
export interface Message {
  /** The event type used to dispatch events with. */
  type: string;
  /** Additional data sent with the message.*/
  detail?: unknown;
  /**  When set to true the event on the receiver side can cancel the request using event#preventDefault() */
  cancelable?: boolean;
}

/**
 * An object that represents a post-message endpoint as a generic substitution for MessagePort.<br>
 */
export type Endpoint = Pick<MessagePort, 'addEventListener' | 'removeEventListener'> & {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  start?: () => void;
};

/**
 * Wrapper for windows to be used as Endpoint as well (like MessagePort).
 *
 * @param window - The window to wrap.
 * @param targetOrigin - Specifies what the origin of targetWindow must be for the event to be dispatched, either as the literal string "*" (indicating no preference) or as a URI.
 * @return An Endpoint for the given window and origin.
 */
export function windowEndpoint (window: Window, targetOrigin: string = location.origin): Endpoint {
  return {
    postMessage: (message, transferables: Transferable[] | undefined) => window.postMessage(message, targetOrigin, transferables),
    addEventListener: self.addEventListener.bind(self),
    removeEventListener: self.removeEventListener.bind(self)
  };
}

/**
 * Sends out a message using a request-response pattern. Times out after TIMEOUT_MS.
 *
 * @param endpoint -
 * @param {object|string} msg -  The message or the type of the message to be sent.
 * @param transferables -  Transferable objects to be transferred — these objects have their ownership transferred to the receiving browsing context, so are no longer usable by the sending browsing context.
 * @return {Promise.<boolean>} A promise reflecting the receiving of the request on iframe content side. First argument states the cancellation of the request by the recipient via event#preventDefault().
 */
export function doRequest (
  endpoint: Endpoint,
  msg: Message | string,
  transferables: Transferable[] = []
): Promise<boolean> {
  let listener: (event: MessageEvent) => void;
  const id = `${ID_PREFIX}-${uniqueId()}`;

  return new Promise(resolve => {
    endpoint.addEventListener(
      'message',
      listener = event => {
        // Ignore messages with a different id (also filters messages from browser api, etc)
        const data = event.data;
        if (data.id === id && data.request === false) {
          const canceled = data.canceled;
          assertType<boolean>(canceled, typeof canceled === 'boolean', `canceled value must be a boolean: ${canceled}`);
          resolve(canceled);
        }
      });

    if (typeof msg === 'string') {
      msg = { type: msg };
    }

    endpoint.postMessage({ id, request: true, msg }, transferables);
    if (endpoint.start) {
      endpoint.start();
    }
  }).finally(() => endpoint.removeEventListener('message', listener)) as Promise<boolean>;
}

export class IncomingMessagePostMessageApiEvent extends CustomEvent<unknown> {

  /**
   * Promises by which to delay the post message answer.
   *
   * @type {Promise[]}
   */
  _deferResponsePromises: Promise<unknown>[] = [];

  constructor (msg: Message) {
    super(msg.type, {
      cancelable: !!msg.cancelable,
      detail: msg.detail
    });
  }

  /**
   * Adds a Promise to the list of promise by which to delay the post message answer.
   * This message can be used on the receiver side of a message (as event) to defer the resolving of
   * the promise returned by the API on the sender side.
   *
   * @param {Promise} promise - The promise to add
   */
  deferResponse = (promise: Promise<unknown>) => {
    this._deferResponsePromises.push(promise);
  };

  /**
   * Creates a promise that resolves when all registered response Promises are resolved. If no
   * promises are added by calling {@link deferResponse}, the promise is resolved immediately.
   *
   * @return {Promise<Promise[]>} A promise that resolves when all API listeners have reacted to the
   *   message event
   */
  createDeferResponsePromise () {
    return Promise.all(this._deferResponsePromises);
  }

}

/**
 * Post-Message API for communication using a request-response pattern.
 */
export class PostMessageApi extends Observable {

  /**
   * True when we currently are handling requests events. Set to false after. This is useful for
   * avoiding circular messages: The receiver can omit sending a new request depending on the
   * currently handled event.
   */
  eventCurrentlyDispatchedDueToIncomingMessage?: IncomingMessagePostMessageApiEvent;

  #eventKeys: EventKey[] = [];

  #endpoint?: Endpoint;

  _onMessage ({ data: { id, request, msg } }: MessageEvent) {
    if (id === undefined || !id.startsWith(ID_PREFIX)) {
      return; // Ignore other messages send by browser api, etc
    }
    if (!request) {
      return; // Ignore our own responses
    }

    const event = new IncomingMessagePostMessageApiEvent(msg);
    const endpoint = this.#endpoint; // Fetch endpoint before event because it might change in following event handlers

    this.eventCurrentlyDispatchedDueToIncomingMessage = event;
    const canceled = !this.dispatchEvent(event);
    this.eventCurrentlyDispatchedDueToIncomingMessage = undefined;

    event.createDeferResponsePromise()
      .finally(() => {
        endpoint?.postMessage({ id, request: false, canceled });
      });
  }

  set endpoint (newEndpoint: Endpoint | undefined) {
    if (newEndpoint) {
      assert(typeof newEndpoint.addEventListener === 'function'
        && typeof newEndpoint.removeEventListener === 'function'
        && typeof newEndpoint.postMessage === 'function', 'value is not a valid endpoint');
    }

    unByKey(this.#eventKeys);
    this.#eventKeys = [];

    if (newEndpoint) {
      this.#eventKeys.push(
        // newEndpoint is not really an EventTarget, but it has the required methods
        // The "correct" thing to do would be to change the signature of on()
        on(newEndpoint as unknown as EventTarget, 'message', (e: MessageEvent) => this._onMessage(e))
      );

      if (newEndpoint.start) {
        newEndpoint.start();
      }
    }

    this.#endpoint = newEndpoint;
  }

  /**
   * @protected
   * @return {Endpoint} The set endpoint
   */
  get endpoint () {
    return this.#endpoint;
  }

  /**
   * Sends out a message using a request-response pattern. Times out after TIMEOUT_MS.
   *
   * @param msg - The message to be sent.
   * @param transferables - Transferable objects to be transferred — these objects
   * have their ownership transferred to the receiving browsing context,
   * so are no longer usable by the sending browsing context.
   * @return A promise reflecting the receiving of the request on iframe content side.
   * First argument states the cancellation of the request by the recipient via event#preventDefault().
   */
  request (msg: Message, transferables?: Transferable[]): Promise<boolean | NoEndpointDefinedError> {
    if (!this.#endpoint) {
      return Promise.reject(new NoEndpointDefinedError(msg));
    }
    return doRequest(this.#endpoint, msg, transferables);
  }

}

export class NoEndpointDefinedError extends CustomError {

  constructor (message: Message) {
    super(`No endpoint set ${message ? ': ' + message : ''}`);
  }

}
